import { useCmrServiceCmrTariffsList } from '@insurance/api/queries'
import { CMRVehicleCategoryEnum } from '@insurance/api/requests'
import {
  EditVehicleBrandSelect,
  EditVehicleModelSelect
} from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { Text } from '@shared/components'
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography
} from 'antd'
import dayjs from 'dayjs'
import { Trash } from 'iconsax-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { formProps } from './utils'

const { Title } = Typography

type VehiclesQuantityRange = { lower: number | null; upper: number | null }

type VehicleRowProps = {
  name: number
  onRemove: () => void
  disableRemove?: boolean
}

const VehicleRow = ({ name, onRemove, disableRemove }: VehicleRowProps) => {
  const { t } = useTranslation()
  const { required } = useValidationsField()
  const brand = Form.useWatch(['vehicles', name, 'brand'], { preserve: true })

  const vehicleCategoryOptions = [
    { label: t('truck'), value: CMRVehicleCategoryEnum.TRUCK },
    { label: t('trailer'), value: CMRVehicleCategoryEnum.TRAILER }
  ]

  return (
    <Row gutter={[12, 0]} align="middle">
      <Col xs={24} lg={4}>
        <Form.Item
          name={[name, 'category']}
          label={t('category')}
          rules={[required]}
        >
          <Select
            placeholder={t('category')}
            options={vehicleCategoryOptions}
          />
        </Form.Item>
      </Col>

      <Col xs={24} lg={4}>
        <Form.Item name={[name, 'brand']} label={t('brand')} rules={[required]}>
          <EditVehicleBrandSelect placeholder={t('brand')} />
        </Form.Item>
      </Col>

      <Col xs={24} lg={4}>
        <Form.Item name={[name, 'model']} label={t('model')} rules={[required]}>
          <EditVehicleModelSelect
            brand={brand?.toUpperCase()}
            placeholder={t('model')}
          />
        </Form.Item>
      </Col>

      <Col xs={24} lg={6}>
        <Form.Item
          name={[name, 'registration_number']}
          label={t('registration_number')}
          rules={[required]}
        >
          <Input placeholder={t('registration_number')} />
        </Form.Item>
      </Col>

      <Col xs={20} lg={4}>
        <Form.Item name={[name, 'year']} label={t('year')} rules={[required]}>
          <DatePicker.YearPicker
            maxDate={dayjs()}
            placeholder={t('year')}
            className="w-full"
          />
        </Form.Item>
      </Col>

      <Col xs={4} lg={2}>
        <Form.Item label=" ">
          <Button
            type="text"
            danger
            disabled={disableRemove}
            icon={<Trash size={16} />}
            onClick={onRemove}
          />
        </Form.Item>
      </Col>
    </Row>
  )
}

export const Vehicles = () => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()

  const {
    vehiclesProps,
    periodProps,
    totalAmountProps,
    perCaseAmountProps,
    generalCargoProps,
    highRiskCargoProps
  } = formProps(t)

  const period = Form.useWatch(periodProps.name)
  const totalAmount = Form.useWatch(totalAmountProps.name)
  const perCaseAmount = Form.useWatch(perCaseAmountProps.name)
  const generalCargo = Form.useWatch(generalCargoProps.name)
  const highRiskCargo = Form.useWatch(highRiskCargoProps.name)

  let category: 'general_cargo' | 'high_risk_cargo' | undefined
  if (highRiskCargo) {
    category = 'high_risk_cargo'
  } else if (generalCargo) {
    category = 'general_cargo'
  }

  const { data: tariffs = [] } = useCmrServiceCmrTariffsList({
    period,
    category,
    perCaseAmount,
    totalAmount
  })

  const matchingTariffs = tariffs.filter(
    (tariff) =>
      tariff.total_amount === totalAmount &&
      tariff.per_case_amount === perCaseAmount
  )

  const { minVehicles, maxVehicles } = matchingTariffs.reduce<{
    minVehicles: number
    maxVehicles: number | undefined
  }>(
    (acc, tariff) => {
      const range = tariff.vehicles_quantity as VehiclesQuantityRange

      return {
        minVehicles:
          range.lower === null
            ? acc.minVehicles
            : Math.min(acc.minVehicles, range.lower),
        maxVehicles:
          acc.maxVehicles === undefined || range.upper === null
            ? undefined
            : Math.max(acc.maxVehicles, range.upper)
      }
    },
    { minVehicles: Infinity, maxVehicles: 0 }
  )

  const normalizedMin = Number.isFinite(minVehicles) ? minVehicles : 1
  const normalizedMax =
    maxVehicles === 0 || maxVehicles === undefined ? undefined : maxVehicles - 1

  useEffect(() => {
    if (normalizedMax === undefined) return
    const current: unknown[] =
      formInstance.getFieldValue(vehiclesProps.name) ?? []
    if (current.length > normalizedMax) {
      formInstance.setFieldValue(
        vehiclesProps.name,
        current.slice(0, normalizedMax)
      )
    }
  }, [normalizedMax, formInstance, vehiclesProps.name])

  return (
    <>
      <Title level={5}>{t('vehicles')}</Title>

      <Form.List
        name={vehiclesProps.name}
        rules={[
          {
            validator: async (_, value) => {
              if (!value?.length) {
                return Promise.reject(new Error(t('add_at_least_one_vehicle')))
              }
            }
          }
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map(({ key, name }) => (
              <VehicleRow
                key={key}
                name={name}
                onRemove={() => remove(name)}
                disableRemove={fields.length <= normalizedMin}
              />
            ))}

            <Space direction="vertical" style={{ width: '100%' }}>
              {errors.map((error, index) => (
                <Text key={index} type="danger">
                  {error}
                </Text>
              ))}
              <Button
                type="dashed"
                block
                onClick={() => add()}
                disabled={
                  normalizedMax !== undefined && fields.length >= normalizedMax
                }
              >
                + {t('add_vehicle')}
              </Button>
            </Space>
          </>
        )}
      </Form.List>
    </>
  )
}
